/**
 * Block envelope cache — the single source of truth for `SimpleBlockEnvelope`
 * rows across the viewer.
 *
 * Architectural shape:
 *
 *   - `blockCollection` (this file) is a pure key-value envelope cache.
 *     Nothing evicts entries except an explicit `writeDelete`.
 *   - Pointer-only stores (`mainStreamStore`, `collectionBlocksCollection`,
 *     `pinStore`, smart-collection scopes, search results) hold lists of
 *     blockIds and resolve envelopes through `blockStore.useById` / `.get`,
 *     which read this collection. They do NOT have their own envelope
 *     storage.
 *   - Every fetcher that lands envelopes — `mainStreamStore`'s seed,
 *     `collectionStore.loadBlocks`, `pinnedStreamStore.warm`,
 *     `loadBlocksByIds`, `ensureLoaded`, optimistic capture in
 *     `blockStore.add` — writes through `blockStore.upsert` / `upsertMany`,
 *     which call the manual-sync `utils.write*` helpers below.
 *
 * Why manual sync (and not `queryCollectionOptions`):
 *
 *   The previous shape used `queryCollectionOptions({ queryFn:
 *   getInitialBlocksPromise })`, which made the homepage's top-30 the
 *   authoritative state of the *entire* cache. Every time that queryFn
 *   settled (cold-load seed, focus refetch, any invalidation) the
 *   query-db-collection adapter diff-synced the collection to that 30-block
 *   result and emitted a `delete` for every envelope NOT in those 30 —
 *   wiping IDB-hydrated collection blocks, pinned blocks, search results,
 *   smart-collection envelopes, and any other side-loaded data as a side
 *   effect.
 *
 *   The `installPinnedEnvelopeProtection` subscriber in
 *   `pinned-stream-store.ts` was a per-feature workaround for exactly this
 *   eviction; collection-detail had no such guard, which is why cards
 *   "flashed and disappeared" on window focus / SW controllerchange / any
 *   refetch. Moving to manual sync deletes the eviction mechanism and the
 *   workaround in one shot — the bug class doesn't exist here anymore.
 *
 *   `getInitialBlocksPromise()` lives on in `initial-blocks-api.ts` and is
 *   still used by `mainStreamStore.useList`'s seed effect; it's just no
 *   longer a sync source for this collection.
 *
 * IDB persistence (`idb-persistence.ts`) snapshots the collection on every
 * change (debounced) and replays the snapshot at module load. Replay is a
 * single batched write via the manual sync — no React Query indirection,
 * no race against a queryFn. Cards paint from cache in ~20 ms on warm
 * visits.
 */
import { createCollection } from "@tanstack/react-db";
import { debounce, readBlockEnvelopesSnapshot, writeBlockEnvelopesSnapshot, } from "./idb-persistence";
/**
 * How long to coalesce writes before flushing to IndexedDB. A
 * `writeBatch` of 100 envelopes shouldn't write 100 times — wait for
 * the burst to settle, then snapshot the full collection state in
 * one pass.
 */
const IDB_PERSIST_DEBOUNCE_MS = 250;
let syncBegin = null;
let syncWrite = null;
let syncCommit = null;
let syncCollection = null;
let inBatch = false;
function writeUpsertImpl(envelope) {
    if (syncBegin === null || syncWrite === null || syncCommit === null)
        return;
    const existing = syncCollection?.get(envelope.block.id);
    const type = existing === undefined ? "insert" : "update";
    if (inBatch === false)
        syncBegin();
    syncWrite({ type, value: envelope });
    if (inBatch === false)
        syncCommit();
}
function writeDeleteImpl(id) {
    if (syncBegin === null || syncWrite === null || syncCommit === null)
        return;
    const existing = syncCollection?.get(id);
    if (existing === undefined)
        return;
    if (inBatch === false)
        syncBegin();
    syncWrite({ type: "delete", value: existing });
    if (inBatch === false)
        syncCommit();
}
function writeBatchImpl(callback) {
    if (syncBegin === null || syncCommit === null) {
        callback();
        return;
    }
    if (inBatch === true) {
        // Nested batches flatten into the outer transaction.
        callback();
        return;
    }
    inBatch = true;
    syncBegin();
    try {
        callback();
    }
    finally {
        syncCommit();
        inBatch = false;
    }
}
// ---------------------------------------------------------------------------
// Collection
// ---------------------------------------------------------------------------
export const blockCollection = createCollection({
    id: "block-envelopes",
    getKey: (item) => item.block.id,
    startSync: true,
    gcTime: 0,
    sync: {
        sync: (params) => {
            syncBegin = params.begin;
            syncWrite = params.write;
            syncCommit = params.commit;
            syncCollection = params.collection;
            params.markReady();
            return () => {
                syncBegin = null;
                syncWrite = null;
                syncCommit = null;
                syncCollection = null;
            };
        },
        getSyncMetadata: () => ({}),
    },
    utils: {
        writeUpsert: writeUpsertImpl,
        // Alias for callers that know the key is new (e.g. optimistic capture
        // placeholders). Same semantics as upsert — the existing-key check
        // inside `writeUpsertImpl` picks "insert" vs "update" message type so
        // a key collision still updates rather than throwing.
        writeInsert: writeUpsertImpl,
        writeDelete: writeDeleteImpl,
        writeBatch: writeBatchImpl,
    },
});
// ---------------------------------------------------------------------------
// IDB persistence — write side
//
// `subscribeChanges` fires after every commit; we don't care which keys
// changed, just that the current state is the new truth. The debounced
// snapshot re-serializes the whole map in one IDB write.
// ---------------------------------------------------------------------------
const flushSnapshot = debounce(() => {
    void writeBlockEnvelopesSnapshot(Array.from(blockCollection.values()));
}, IDB_PERSIST_DEBOUNCE_MS);
blockCollection.subscribeChanges(() => {
    flushSnapshot();
});
// ---------------------------------------------------------------------------
// IDB persistence — read side
//
// Replay the last known snapshot at module load so cards paint from cache
// before any network call fires. `startSync: true` above guarantees the
// sync handles are wired before this async function's body runs (IDB read
// is a microtask, the sync callback is synchronous inside
// `createCollection`), so writes here always reach the collection.
//
// `blockEnvelopesHydratedPromise` is the public handle other modules
// await to coordinate ordering against the IDB replay. `mainStreamStore`'s
// own hydration uses it to make sure cached envelopes are addressable
// before it exposes its persisted id list — without that ordering, the
// stream's live query would briefly emit `[]` (id list populated, but
// `blockCollection` still empty), flashing the "No blocks to show"
// empty state for one render tick. The promise resolves in both
// success and failure paths so callers can treat it as a "hydration
// settled" signal rather than a "hydration succeeded" gate.
// ---------------------------------------------------------------------------
async function hydrateFromIdb() {
    try {
        const cached = await readBlockEnvelopesSnapshot();
        if (cached.length === 0)
            return;
        writeBatchImpl(() => {
            for (const envelope of cached)
                writeUpsertImpl(envelope);
        });
    }
    catch (e) {
        console.warn("[block-collection] IDB hydrate failed:", e);
    }
}
export const blockEnvelopesHydratedPromise = hydrateFromIdb();
